import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MONO_FONT, headCellSx, numCellSx, textCellSx } from "../../components/metrics/metricsUi";
import { purchaseOrderAnalysisService } from "../../utils/services/purchaseOrderAnalysisService";
import {
  COVERAGE_FILTERS,
  LONG_STOCKOUT,
  COVERAGE_SEGMENTS,
  RISK_ROW_LABEL,
  buildRowsParams,
  coverageColor,
  fmtDays,
  fmtMoney,
  fmtShortDay,
  fmtUnits,
  riskColor,
} from "../../utils/purchaseOrderAnalysisPageDefaults";
import { FilterPill, SectionCard, StatusChip, useLatestRequest } from "./parts";

const SEARCH_DEBOUNCE_MS = 300;

const COLUMNS = [
  { id: "sku", label: "SKU", sort: "sku" },
  { id: "on_hand", label: "On hand", sort: "on_hand", num: true, width: 80 },
  { id: "days_cover", label: "Cover", sort: "days_cover", num: true, width: 76 },
  { id: "stockout", label: "Runs out", width: 84, tip: "Today + days of cover" },
  { id: "status", label: "PO coverage", width: 160 },
  { id: "next", label: "Next PO", sort: "next_expected_date", width: 150, tip: "Earliest open PO line for this SKU at this warehouse" },
  { id: "gap", label: "Gap", sort: "gap_days", num: true, width: 76, tip: "Days between running out and the next PO landing; ≈ units short" },
  { id: "suggested_qty", label: "Suggest", sort: "suggested_qty", num: true, width: 88, tip: "Suggested order quantity: daily sales × (horizon + 30 days) − on hand − reliable inbound" },
  { id: "revenue_30d", label: "30d rev.", sort: "revenue_30d", num: true, width: 96 },
];

/** One risk row of the matrix: a stacked bar of its SKUs by coverage status. */
const MatrixRow = ({ row, statuses, ui, onPick, active, maxTotal }) => {
  const total = row.total || 0;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "128px 1fr 56px", alignItems: "center", gap: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: riskColor(row.risk), flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: ui.colors.textPrimary, whiteSpace: "nowrap" }}>
          {RISK_ROW_LABEL[row.risk] || row.risk}
        </Typography>
      </Box>
      {/* 2px surface gaps separate segments; a segment is a button that filters the table. */}
      {/* Bar length is the row's share of the largest row, so band sizes compare. */}
      <Box sx={{ display: "flex", gap: "2px", height: 22, width: `${Math.max(3, (total / (maxTotal || 1)) * 100)}%` }}>
        {statuses
          .filter((status) => row[status] > 0)
          .map((status, index, list) => {
            const isActive = active && active.risk === row.risk && active.status === status;
            return (
              <Tooltip
                key={status}
                arrow
                placement="top"
                title={`${RISK_ROW_LABEL[row.risk]} · ${COVERAGE_SEGMENTS.find((s) => s.key === status)?.short}: ${fmtUnits(row[status])} SKUs — click to list`}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={`${row.risk} ${status} ${row[status]} SKUs`}
                  onClick={() => onPick(row.risk, status)}
                  sx={{
                    flexGrow: row[status],
                    flexBasis: 0,
                    minWidth: 6,
                    border: 0,
                    p: 0,
                    cursor: "pointer",
                    backgroundColor: coverageColor(status, ui.darkMode),
                    opacity: active && !isActive ? 0.35 : 1,
                    borderRadius: `${index === 0 ? 4 : 0}px ${index === list.length - 1 ? 4 : 0}px ${
                      index === list.length - 1 ? 4 : 0
                    }px ${index === 0 ? 4 : 0}px`,
                    "&:hover": { opacity: 1, filter: "brightness(1.08)" },
                  }}
                />
              </Tooltip>
            );
          })}
      </Box>
      <Typography sx={{ fontFamily: MONO_FONT, fontSize: 12, color: ui.colors.textSecondary, textAlign: "right" }}>
        {fmtUnits(total)}
      </Typography>
    </Box>
  );
};

const CoverageSection = ({ ui, base, overview, statusFilter, onStatusFilter, onOpenSku, mobile }) => {
  const { colors } = ui;
  const coverage = overview.coverage;
  const horizon = overview.horizon;
  const [riskFilter, setRiskFilter] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "priority", dir: "asc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, riskFilter, base.warehouse_id, base.months]);

  const rows = useLatestRequest(
    base.warehouse_id
      ? (signal) =>
          purchaseOrderAnalysisService.fetchRows(
            buildRowsParams({
              base,
              table: "coverage",
              page,
              rowsPerPage,
              sort: sort.field,
              dir: sort.dir,
              search,
              status: statusFilter,
              risk: riskFilter,
            }),
            { signal },
          )
      : null,
    [base.warehouse_id, base.client_id, base.months, page, rowsPerPage, sort, search, statusFilter, riskFilter],
  );

  const pickCell = (risk, status) => {
    const same = riskFilter === risk && statusFilter === status;
    setRiskFilter(same ? null : risk);
    onStatusFilter(same ? "all" : status);
  };
  const toggleSort = (field) =>
    setSort((current) =>
      current.field === field
        ? { field, dir: current.dir === "desc" ? "asc" : "desc" }
        : { field, dir: field === "sku" || field === "next_expected_date" ? "asc" : "desc" },
    );

  const needsPo = coverage.needs_po;
  const evaluated = coverage.evaluated_skus;
  const horizonText =
    horizon.source === "median_lead_time"
      ? `${horizon.days} days — this warehouse's median PO lead time`
      : `${horizon.days} days (not enough received POs to measure lead time)`;
  const counts = {
    all: evaluated,
    needs_po: needsPo,
    late: coverage.late,
    pending: coverage.pending,
    covered: coverage.covered,
  };
  const activeCell = riskFilter && statusFilter !== "all" ? { risk: riskFilter, status: statusFilter } : null;
  const data = rows.data;

  const cell = (row, column) => {
    switch (column.id) {
      case "sku":
        return (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 650, fontFamily: MONO_FONT, fontSize: 12.5, overflowWrap: "anywhere" }}>{row.sku}</Box>
            <Box
              title={row.product_name || ""}
              sx={{ fontSize: 11.5, color: colors.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              <Box component="span" sx={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", backgroundColor: riskColor(row.risk), mr: 0.6 }} />
              {row.product_name || "—"}
            </Box>
          </Box>
        );
      case "days_cover":
        return row.risk === "out_of_stock" || row.risk === LONG_STOCKOUT ? (
          <Box component="span" sx={{ color: colors.textSecondary }}>Out</Box>
        ) : (
          fmtDays(row.days_cover)
        );
      case "stockout":
        if (row.risk === LONG_STOCKOUT) {
          return (
            <Box sx={{ lineHeight: 1.3 }}>
              <Box>All month</Box>
              <Box sx={{ fontSize: 10.5, color: colors.textTertiary }}>sold {fmtShortDay(row.last_sale_date)}</Box>
            </Box>
          );
        }
        return row.risk === "out_of_stock" ? "Now" : fmtShortDay(row.stockout_date);
      case "status":
        return (
          <StatusChip
            ui={ui}
            color={coverageColor(row.status, ui.darkMode)}
            label={COVERAGE_SEGMENTS.find((s) => s.key === row.status)?.short}
          />
        );
      case "next":
        if (!row.next_po_no) return <Box component="span" sx={{ color: colors.textTertiary }}>—</Box>;
        return (
          <Box sx={{ lineHeight: 1.35 }}>
            <Box sx={{ fontFamily: MONO_FONT, fontSize: 12 }}>{row.next_po_no}</Box>
            <Box sx={{ fontSize: 11.5, color: colors.textTertiary }}>
              {row.next_expected_date
                ? `${fmtShortDay(row.next_expected_date)} · ${fmtUnits(row.next_units)} u`
                : row.days_overdue
                  ? `${row.days_overdue}d overdue`
                  : "no date"}
            </Box>
          </Box>
        );
      case "gap":
        return row.gap_days == null ? (
          <Box component="span" sx={{ color: colors.textTertiary }}>—</Box>
        ) : (
          <Box sx={{ lineHeight: 1.35 }}>
            <Box>{row.gap_days}d</Box>
            <Box sx={{ fontSize: 11, color: colors.textTertiary }}>≈{fmtUnits(row.units_short)} u</Box>
          </Box>
        );
      case "suggested_qty":
        return row.suggested_qty > 0 ? (
          <Box component="span" sx={{ fontWeight: 700 }}>{fmtUnits(row.suggested_qty)}</Box>
        ) : (
          <Box component="span" sx={{ color: colors.textTertiary }}>—</Box>
        );
      case "revenue_30d":
        return row.risk === LONG_STOCKOUT ? (
          <Tooltip title="Last known 30 days (up to its last sale) — it has sold nothing this month" arrow>
            <Box component="span" sx={{ color: colors.textSecondary }}>{fmtMoney(row.revenue_30d)}*</Box>
          </Tooltip>
        ) : (
          fmtMoney(row.revenue_30d)
        );
      default:
        return fmtUnits(row[column.id]);
    }
  };

  return (
    <SectionCard
      ui={ui}
      title="Replenishment coverage"
      subtitle={
        evaluated ? (
          <>
            <strong>{fmtUnits(needsPo)} of {fmtUnits(evaluated)}</strong> SKUs that will run out within{" "}
            {horizonText} have nothing reliable on order —{" "}
            <strong>{fmtMoney(coverage.revenue_30d_by_status.none + coverage.revenue_30d_by_status.unconfirmed)}</strong>{" "}
            of 30-day revenue.
            {coverage.long_stockouts?.skus ? (
              <>
                {" "}
                Separately, <strong>{fmtUnits(coverage.long_stockouts.skus)}</strong> SKUs have been out of stock
                all month after selling before it ({fmtUnits(coverage.long_stockouts.needs_po)} with no reliable PO).
              </>
            ) : null}
          </>
        ) : (
          `No selling SKU here runs out within ${horizonText}.`
        )
      }
    >
      {evaluated ? (
        <Box sx={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "minmax(0, 1fr) 260px", gap: 3, mb: 2.25 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "128px 1fr 56px", gap: 1.25 }}>
              <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>Stock position</Typography>
              <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>SKUs by PO coverage →</Typography>
              <Typography sx={{ fontSize: 11, color: colors.textTertiary, textAlign: "right" }}>SKUs</Typography>
            </Box>
            {coverage.matrix.map((row) => (
              <MatrixRow
                key={row.risk}
                row={row}
                statuses={coverage.statuses}
                ui={ui}
                onPick={pickCell}
                active={activeCell}
                maxTotal={Math.max(...coverage.matrix.map((item) => item.total))}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, justifyContent: "center" }}>
            {COVERAGE_SEGMENTS.map((segment) => (
              <Box key={segment.key} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "3px", mt: "3px", flexShrink: 0, backgroundColor: coverageColor(segment.key, ui.darkMode) }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: colors.textPrimary }}>{segment.short}</Typography>
                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: 12, color: colors.textSecondary }}>
                      {fmtUnits(coverage[segment.key])}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: colors.textTertiary, lineHeight: 1.35 }}>{segment.label}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", mb: 1.25 }}>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
          {COVERAGE_FILTERS.map((filter) => (
            <FilterPill
              key={filter.value}
              ui={ui}
              label={filter.label}
              badge={fmtUnits(counts[filter.value])}
              active={statusFilter === filter.value && !riskFilter}
              color={filter.value === "all" || filter.value === "needs_po" ? null : coverageColor(filter.value, ui.darkMode)}
              onClick={() => {
                setRiskFilter(null);
                onStatusFilter(filter.value);
              }}
            />
          ))}
          {coverage.long_stockouts?.skus ? (
            <FilterPill
              ui={ui}
              label="Out of stock all month"
              badge={fmtUnits(coverage.long_stockouts.skus)}
              color={riskColor(LONG_STOCKOUT)}
              active={riskFilter === LONG_STOCKOUT && statusFilter === "all"}
              onClick={() => {
                setRiskFilter(LONG_STOCKOUT);
                onStatusFilter("all");
              }}
            />
          ) : null}
          {riskFilter && !(riskFilter === LONG_STOCKOUT && statusFilter === "all") ? (
            <FilterPill
              ui={ui}
              active
              color={riskColor(riskFilter)}
              label={`${RISK_ROW_LABEL[riskFilter]} × ${COVERAGE_SEGMENTS.find((s) => s.key === statusFilter)?.short || "all"} ✕`}
              onClick={() => {
                setRiskFilter(null);
                onStatusFilter("all");
              }}
            />
          ) : null}
        </Box>
        <TextField
          size="small"
          placeholder="Search SKU or product…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ width: mobile ? "100%" : 260, m: 0, "& .MuiOutlinedInput-root": { height: 36, fontSize: 13.5 } }}
        />
      </Box>

      <Box sx={{ overflowX: "auto", opacity: rows.loading ? 0.6 : 1, transition: "opacity 120ms" }}>
        <Table size="small" sx={{ tableLayout: "fixed", minWidth: 1060 }}>
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.id} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  sx={headCellSx(ui, { textAlign: column.num ? "right" : "left", whiteSpace: "nowrap", px: 1.25 })}
                >
                  <Tooltip title={column.tip || ""} placement="top" arrow>
                    {column.sort ? (
                      <TableSortLabel
                        active={sort.field === column.sort}
                        direction={sort.field === column.sort ? sort.dir : "desc"}
                        onClick={() => toggleSort(column.sort)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      <span>{column.label}</span>
                    )}
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.error ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={textCellSx(ui, { py: 3, color: ui.danger })}>{rows.error}</TableCell>
              </TableRow>
            ) : !data || !data.rows.length ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={textCellSx(ui, { py: 3 })}>
                  {rows.loading ? "Loading…" : "No SKUs match this filter."}
                </TableCell>
              </TableRow>
            ) : (
              data.rows.map((row) => (
                <TableRow
                  key={row.sku}
                  tabIndex={0}
                  onClick={() => onOpenSku(row.sku)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onOpenSku(row.sku);
                  }}
                  sx={{ cursor: "pointer", "&:hover": { backgroundColor: ui.surfaceAlt } }}
                >
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={
                        column.num
                          ? numCellSx(ui, { fontFamily: MONO_FONT, whiteSpace: "nowrap", px: 1.25 })
                          : textCellSx(ui, { px: 1.25 })
                      }
                    >
                      {cell(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_event, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
      <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, lineHeight: 1.6 }}>
        Stock, sales and days of cover are the same numbers as Inventory Risk by Product. &ldquo;Out all
        month&rdquo; SKUs sold nothing in the 30-day window because they had no stock; their revenue (*) and
        suggested quantity use the last known 30 days before they ran out, and they are not in the counts above. Suggested = daily
        sales × ({horizon.days} + 30 days) − on hand − reliable inbound. Click a SKU for its stock runway and
        full PO history.
      </Typography>
    </SectionCard>
  );
};

export default CoverageSection;
